import React, { useEffect, useState, useCallback, useMemo } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Autocomplete,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import Fuse from "fuse.js";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchAllAssessments } from "../../redux/actions/assessmentActions";
import { fetchOrganizations } from "../../redux/actions/organizationActions";
import CreateAssessmentDialog from "./assessments/CreateAssessmentDialog";

interface AssessmentsTabProps {
  organizationId?: string;
}

const AssessmentsTab: React.FC<AssessmentsTabProps> = ({ organizationId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Initialize search term and org filter from URL parameters
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
  const [filterOrganization, setFilterOrganization] = useState(() => searchParams.get("org") || "");
  const [filterCategory, setFilterCategory] = useState("");

  const { assessments, loading, error } = useSelector(
    (state: RootState) => state.assessments
  );
  const { organizations } = useSelector(
    (state: RootState) => state.organizations
  );

  useEffect(() => {
    // Only pass organizationId if we're in an organization context
    // This allows admin users to see all assessments across organizations
    const params: any = {};
    if (organizationId) {
      params.organizationId = organizationId;
    }

    dispatch(fetchAllAssessments(params));

    // Only fetch organizations if we're not already in an organization context
    if (!organizationId) {
      dispatch(fetchOrganizations());
    }
  }, [dispatch, organizationId]);

  // Sync search term and org filter from URL when it changes externally
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlOrg = searchParams.get("org") || "";
    setSearchTerm(urlSearch);
    setFilterOrganization(urlOrg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Update URL when filters change
  const updateUrlParams = useCallback((search: string, org: string) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (org) params.set("org", org);
    setSearchParams(params);
  }, [setSearchParams]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    updateUrlParams(value, filterOrganization);
  }, [updateUrlParams, filterOrganization]);

  const handleOrganizationFilterChange = useCallback((value: string) => {
    setFilterOrganization(value);
    updateUrlParams(searchTerm, value);
  }, [updateUrlParams, searchTerm]);

  const handleViewAssessment = useCallback(
    (id: string, orgId: string) => {
      navigate(`/dashboard/assessments/${orgId}/${id}`);
    },
    [navigate]
  );

  const handleCreateAssessment = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  const filteredAssessments = useMemo(() => {
    let data = assessments;

    // Filter by organization if organizationId is provided
    if (organizationId) {
      data = data.filter(
        (assessment) => assessment.organization_id === organizationId
      );
    }

    // Separate processing and active assessments
    const processingAssessments = data.filter(
      (assessment) => assessment.generation_status === "pending"
    );

    // For active assessments, group by resource_key and keep only the latest
    const activeAssessments = data.filter(
      (assessment) => 
        assessment.is_active === true && 
        assessment.generation_status === "done"
    );

    // Group active assessments by organization_id + resource_key and keep only the latest
    const latestActiveByOrgAndKey = new Map();
    activeAssessments.forEach((assessment) => {
      // Create a composite key that includes organization_id and resource_key
      const resourceKey = assessment.resource_key || assessment.id; // Use id as fallback if no resource_key
      const orgId = assessment.organization_id || 'no-org'; // Handle cases where org_id might be null
      const compositeKey = `${orgId}::${resourceKey}`;
      
      const existing = latestActiveByOrgAndKey.get(compositeKey);
      
      if (!existing || new Date(assessment.creation_time) > new Date(existing.creation_time)) {
        latestActiveByOrgAndKey.set(compositeKey, assessment);
      }
    });

    // Combine processing assessments with latest active assessments
    data = [...processingAssessments, ...Array.from(latestActiveByOrgAndKey.values())];

    // Apply search filter using Fuse.js for fuzzy search
    if (searchTerm) {
      const fuse = new Fuse(data, {
        keys: [
          { name: "display_title", weight: 2 },
          { name: "resource_key", weight: 1.5 },
          { name: "id", weight: 1.5 },
          { name: "scenario_description", weight: 0.8 },
        ],
        threshold: 0.4,
        includeScore: true,
        ignoreLocation: true,
      });
      
      const results = fuse.search(searchTerm);
      data = results.map((result) => result.item);
    }

    // Apply organization filter
    if (filterOrganization) {
      data = data.filter(
        (assessment) => assessment.organization_id === filterOrganization
      );
    }

    // Apply category filter
    if (filterCategory) {
      data = data.filter(
        (assessment) => assessment.category === filterCategory
      );
    }

    return data;
  }, [
    assessments,
    searchTerm,
    filterOrganization,
    filterCategory,
    organizationId,
  ]);

  // Get unique categories from assessments for the category filter
  const uniqueCategories = useMemo(() => {
    const categories = new Set<string>();
    assessments.forEach((assessment) => {
      if (assessment.category) {
        categories.add(assessment.category);
      }
    });
    return Array.from(categories).sort();
  }, [assessments]);

  const getStatusIcon = (assessment: any) => {
    if (assessment.generation_status === "done" && assessment.is_active) {
      return <Chip label="Active" size="small" color="success" />;
    } else if (assessment.generation_status === "pending") {
      return <Chip label="Processing" size="small" color="warning" />;
    } else if (
      assessment.generation_status === "done" &&
      !assessment.is_active
    ) {
      return <Chip label="Inactive" size="small" color="default" />;
    }
    return <Chip label="Unknown" size="small" color="default" />;
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "status",
        headerName: "Status",
        width: 100,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            {getStatusIcon(params.row)}
          </Box>
        ),
      },
      {
        field: "id",
        headerName: "Assessment ID",
        flex: 1,
        minWidth: 150,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "90%",
                textAlign: "center",
              }}
              title={params.value as string}
            >
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "display_title",
        headerName: "Title",
        flex: 1,
        minWidth: 150,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "90%",
                textAlign: "center",
                fontWeight: params.value ? 500 : 400,
              }}
              title={(params.value as string) || "-"}
            >
              {params.value || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "scenario_description",
        headerName: "Description",
        flex: 1.5,
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "90%",
                textAlign: "center",
                px: 1,
              }}
              title={(params.value as string) || ""}
            >
              {params.value || ""}
            </Typography>
          </Box>
        ),
      },
      {
        field: "category",
        headerName: "Category",
        width: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            {params.value ? (
              <Chip
                label={params.value}
                size="small"
                color="primary"
                variant="outlined"
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </Box>
        ),
      },
      ...(organizationId
        ? []
        : [
            {
              field: "organization_id",
              headerName: "Organization",
              flex: 1,
              minWidth: 150,
              headerAlign: "center",
              align: "center",
              renderCell: (params: GridRenderCellParams) => {
                if (!params.value) return "None";
                const org = organizations.find((o) => o.id === params.value);
                const displayValue = org ? org.name : (params.value as string);
                return (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "90%",
                        textAlign: "center",
                      }}
                      title={displayValue}
                    >
                      {displayValue}
                    </Typography>
                  </Box>
                );
              },
            } as GridColDef,
          ]),
      {
        field: "environment",
        headerName: "Env",
        width: 70,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.875rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "90%",
                textAlign: "center",
              }}
              title={params.value as string}
            >
              {params.value as string}
            </Typography>
          </Box>
        ),
      },
      {
        field: "creation_time",
        headerName: "Created",
        width: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              {params.value
                ? format(new Date(params.value as string), "MM/dd HH:mm")
                : ""}
            </Typography>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        disableColumnMenu: true,
        flex: 0.5,
        minWidth: 100,
        renderCell: (params: GridRenderCellParams) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() =>
                handleViewAssessment(params.row.id, params.row.organization_id)
              }
            >
              View
            </Button>
          </Box>
        ),
        headerAlign: "center",
        align: "center",
      },
    ],
    [handleViewAssessment, organizations, organizationId]
  );

  if (loading && assessments.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Assessments</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateAssessment}
        >
          Create Assessment
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
          width: "100%",
        }}
      >
        <TextField
          label="Search Assessments"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ flex: 1, minWidth: 300 }}
        />

        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel>Organization</InputLabel>
          <Select
            value={filterOrganization}
            onChange={(e) => handleOrganizationFilterChange(e.target.value)}
            label="Organization"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {organizations.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          options={uniqueCategories}
          value={filterCategory || null}
          onChange={(event, newValue) => {
            setFilterCategory(newValue || "");
          }}
          renderInput={(params) => (
            <TextField {...params} label="Category" variant="outlined" />
          )}
          sx={{ minWidth: 200 }}
        />

        <Button
          variant="outlined"
          onClick={() => {
            setSearchTerm("");
            setFilterOrganization("");
            setFilterCategory("");
            setSearchParams({});
          }}
        >
          Reset Filters
        </Button>
      </Box>

      <Box
        sx={{
          height: organizationId ? "40vh" : "calc(100vh - 200px)",
          width: "100%",
        }}
      >
        <DataGrid
          rows={filteredAssessments}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "creation_time", sort: "desc" }],
            },
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            "& .MuiDataGrid-row": {
              minHeight: "52px !important",
            },
            "& .MuiDataGrid-cell": {
              py: 1,
            },
          }}
        />
      </Box>

      <CreateAssessmentDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        organizationId={organizationId}
      />
    </>
  );
};

export default AssessmentsTab;
